import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from '@reduxjs/toolkit/query'
import store from 'store'
import { ApiRequestOptions } from '../../../../openapi/core/ApiRequestOptions'
import { catchErrorCodes } from '../../../../openapi/core/request'
import { handleApiError } from './api-error-handler'

const baseFetchQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  // `credentials: 'include'` is required so the HttpOnly refresh token cookie travels
  // on /authentication/* requests. Browsers also require this on every cross-origin
  // request that needs to read or write cookies for the API origin.
  credentials: 'include',
  prepareHeaders: headers => {
    // Read the access token from Redux memory — never localStorage. After XSS hardening
    // the access token is intentionally non-persistent; it lives only in the slice and
    // is restored at boot via silent refresh against the cookie.
    const token = store.getState().authenticationView.accessToken
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    headers.set('Content-Type', 'application/json')
    headers.set('X-Platform', 'web')
    // Tells the server to set/read the refresh-token cookie instead of putting the
    // token in the response body. Native clients (mobile, CLI) omit this header.
    headers.set('X-Client-Type', 'web')

    return headers
  },
})

export const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const apiFunction = async () => {
    const result = await baseFetchQuery(args, api, extraOptions)

    if (result.meta?.response) {
      catchErrorCodes(args as ApiRequestOptions, result.meta.response)
    }

    return result.data
  }

  try {
    const data = await apiFunction()
    return { data }
  } catch (error) {
    try {
      const data = await handleApiError(error, apiFunction, [], null)
      return { data }
    } catch (handledError) {
      return { error: handledError as FetchBaseQueryError }
    }
  }
}
